import { useCallback, useEffect, useRef, useState } from "react";
import {
  getEnrichmentQueueCounts,
  type EnrichmentQueueCounts,
} from "@/api/enrichment";

export interface EnrichmentQueueCountsState {
  /** Null until the first successful read. */
  counts: EnrichmentQueueCounts | null;
  /** Re-read both counters. Fire it after anything that reserves or
   *  releases rows — a start, an end, a skip. */
  refresh: () => void;
}

/**
 * How many rows each enrichment queue can hand out right now.
 *
 * Fetched once for the whole page rather than per tab: one endpoint
 * returns both numbers, and both queue tabs stay mounted at the same
 * time, so a hook per tab would double every request for nothing.
 *
 * A failed read keeps the last known numbers instead of blanking them.
 * The counter is context for the Start button, not the operator's work —
 * a red error where a number used to be would be louder than the problem.
 */
export function useEnrichmentQueueCounts(
  slug: string,
  enabled = true,
): EnrichmentQueueCountsState {
  const [counts, setCounts] = useState<EnrichmentQueueCounts | null>(null);

  // Refreshes are fired from session actions, so two can be in flight at
  // once (skip, then end). Only the newest answer may land.
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (!slug || !enabled) return;
    const id = ++requestId.current;
    getEnrichmentQueueCounts(slug)
      .then((res) => {
        if (!mounted.current || id !== requestId.current) return;
        setCounts(res);
      })
      .catch((err) => {
        console.warn("[Enrichment] queue counts load failed", err);
      });
  }, [slug, enabled]);

  // Drop the previous project's numbers rather than showing them under
  // the new project's Start button while the fetch is in flight.
  useEffect(() => {
    setCounts(null);
    refresh();
  }, [refresh]);

  return { counts, refresh };
}
